import { blockLetterOnDocumentVerifiers, maskCNPJ, maskCPF } from 'src/helpers/string-helpers';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormGroup, FormGroupDirective, ValidatorFn, Validators } from '@angular/forms';
import { Subscription, distinctUntilChanged } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { InvalidFieldMessageService } from 'src/app/services/invalid-field-message/invalid-field-message.service';
import { CnpjWsService } from 'src/app/services/cnpj-ws/cnpj-ws.service';
import { CnpjValidator, unmaskCnpj } from 'src/validators/cnpj/cnpj';
import { CpfValidator } from 'src/validators/cpf/cpf';
@Component({
  selector: 'app-company-document',
  templateUrl: './company-document.component.html',
  styleUrls: ['./company-document.component.scss'],
})
export class CompanyDocumentComponent implements OnInit, OnDestroy {
  @ViewChild('documentInput') documentInput?: ElementRef;

  get messageError() {
    return this.messageService;
  }
  @Input() dataTest!: string;
  @Input() showIcon: boolean = false;
  @Input() controlName!: string;
  @Input() label!: string;
  @Input() required: boolean = true;
  @Input() autoFillCompanyData: boolean = false;
  @Input() companyNameControl: string = 'companyName';
  @Input() fantasyNameControl: string = 'fantasyName';
  @Input() stateRegistrationControl: string = 'stateRegistration';
  @Input() addressGroupControl: string = 'address';
  @Output() blurOrEnter = new EventEmitter<string>();
  @Output() companyDataFilled = new EventEmitter<void>();
  form!: FormGroup;
  formControlDocument!: AbstractControl
  buscandoCnpj: boolean = false;
  private valueChanges$!: Subscription;
  private validators: ValidatorFn[] = [];
  private ultimoCnpjBuscado: string | null = null;
  private ultimoEstadoBuscado: string | null = null;
  private ultimoCnpjComErro: string | null = null;

  constructor(private rootFormGroup: FormGroupDirective,
    private messageService: InvalidFieldMessageService,
    private cnpjWsService: CnpjWsService,
    private snackBar: MatSnackBar,
  ) { }

  ngOnDestroy(): void {
    this.valueChanges$.unsubscribe();
  }

  ngOnInit(): void {
    this.form = this.rootFormGroup.control;
    this.formControlDocument = this.form.controls[this.controlName];
    this.formatIfHasValue(this.formControlDocument.value)
    this.setValidators();
    this.detectValueChanges();
  }

  private formatIfHasValue(document: string) {
    if (document) {
      const raw = document.trim().replace(/\s+/g, '').replace(/[.\-/]/g, '').toUpperCase();
      const hasLetter = /[A-Z]/.test(raw);
      if (!hasLetter && raw.length <= 11) {
        this.formControlDocument.setValue(maskCPF(raw));
      } else {
        this.formControlDocument.setValue(maskCNPJ(raw));
      }
    }
  }

  private setValidators() {
    this.validators = [
      Validators.maxLength(18),
      Validators.minLength(14),
    ];

    if (this.required)
      this.validators.push(Validators.required)

    this.formControlDocument.setValidators(this.validators);
    // this.formControlDocument.setAsyncValidators([
    //   this.documentValidator.isAvailable()
    // ]); Setado essa validação no signup
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    blockLetterOnDocumentVerifiers(event, this.formControlDocument.value);
  }

  onBlurOrEnter(): void {
    const value = (this.formControlDocument.value ?? '').toString().trim();
    if (value !== this.formControlDocument.value) {
      this.formControlDocument.setValue(value, { emitEvent: false });
    }
    this.blurOrEnter.emit(value);
    if (this.autoFillCompanyData) {
      this.tryAutoFillCompany();
    }
  }

  public focus(): void {
    this.documentInput?.nativeElement.focus();
  }

  private async tryAutoFillCompany(): Promise<void> {
    const cnpj = unmaskCnpj(this.formControlDocument.value ?? '');
    //Só consulta a API quando o CNPJ estiver completo e válido (evita estourar o rate limit)
    if (cnpj.length !== 14) return;
    if (CnpjValidator.isValid(this.formControlDocument)) return;

    const addressGroup = this.form.controls[this.addressGroupControl] as FormGroup | undefined;
    const estadoAtual: string = (addressGroup?.controls['state']?.value ?? '').toString().trim().toUpperCase();

    const camposVazios = [this.companyNameControl, this.fantasyNameControl, this.stateRegistrationControl].some(
      name => !this.form.controls[name]?.value
    );

    const cnpjMudou = cnpj !== this.ultimoCnpjBuscado;
    const estadoMudou = estadoAtual !== this.ultimoEstadoBuscado;

    //Já tentou esse mesmo CNPJ/UF e falhou (não encontrado ou erro técnico) - não insiste sozinho, evita martelar a API
    if (cnpj === this.ultimoCnpjComErro && !estadoMudou) return;
    if (!cnpjMudou && !camposVazios && !estadoMudou) return;
    if (this.buscandoCnpj) return;

    this.buscandoCnpj = true;
    try {
      const empresa = await this.cnpjWsService.consultarCnpj(cnpj);

      //Descarta a resposta se o CNPJ foi alterado enquanto a busca estava em andamento
      if (cnpj !== unmaskCnpj(this.formControlDocument.value ?? '')) return;

      if (!empresa) {
        this.ultimoCnpjComErro = cnpj;
        this.ultimoEstadoBuscado = estadoAtual;
        this.snackBar.open('Erro ao buscar dados do CNPJ', '', {
          duration: 3000,
          verticalPosition: 'top',
          panelClass: ['warning-snackbar']
        });
        return;
      }

      this.ultimoCnpjComErro = null;

      const inscricaoFiltrada = empresa.inscricoesEstaduais.length > 0
        ? (empresa.inscricoesEstaduais.find(ie => ie.uf?.toUpperCase() === estadoAtual) ?? empresa.inscricoesEstaduais[0])
        : null;

      this.form.patchValue({
        [this.companyNameControl]: empresa.razaoSocial,
        [this.fantasyNameControl]: empresa.nomeFantasia?.trim() || empresa.razaoSocial,
        [this.stateRegistrationControl]: inscricaoFiltrada?.numero ?? '',
      });

      this.ultimoCnpjBuscado = cnpj;
      this.ultimoEstadoBuscado = estadoAtual;

      this.companyDataFilled.emit();
    } finally {
      this.buscandoCnpj = false;
    }
  }

  detectValueChanges() {
    this.valueChanges$ = this.formControlDocument.valueChanges.pipe(distinctUntilChanged())
      .subscribe(value => {
        //Remove espaços de início/fim e colados no meio (ex.: valor colado de outro lugar) antes de qualquer processamento
        if (typeof value === 'string' && /\s/.test(value)) {
          value = value.trim().replace(/\s+/g, '');
          this.formControlDocument.setValue(value, { emitEvent: false });
        }

        if (this.autoFillCompanyData && unmaskCnpj(value ?? '').length !== 14) {
          this.ultimoCnpjBuscado = null;
          this.ultimoEstadoBuscado = null;
          this.ultimoCnpjComErro = null;
        }
        if (!this.required) {
          this.formControlDocument.clearValidators();
        }
        if (value) {
          this.formControlDocument.clearValidators();

          const rawFull = value.replace(/[.\-/]/g, '').toUpperCase();
          // Garante que posições 13-14 (DVs) sejam somente dígitos
          const raw = rawFull.length > 12
            ? rawFull.substring(0, 12) + rawFull.substring(12).replace(/[^0-9]/g, '')
            : rawFull;
          const hasLetter = /[A-Z]/.test(raw);
          if (value.length <= 0) {
            this.formControlDocument.setValidators(this.validators);
          } else if (hasLetter || raw.length > 11) {
            // CNPJ: numérico ou alfanumérico
            this.formControlDocument.setValidators([
              ...this.validators,
              Validators.pattern(/^[A-Z0-9]{2}\.[A-Z0-9]{3}\.[A-Z0-9]{3}\/[A-Z0-9]{4}-\d{2}$/),
              CnpjValidator.isValid
            ]);
            this.formControlDocument.setValue(maskCNPJ(value));
          } else {
            // CPF: somente dígitos
            this.formControlDocument.setValidators([
              ...this.validators,
              Validators.pattern(/^\d{3}\.\d{3}\.\d{3}\-\d{2}$/),
              CpfValidator.isValid,
            ]);
            this.formControlDocument.setValue(maskCPF(value));
          }
        }

      });
  }

}
